import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, Output, SimpleChanges }
	from '@angular/core';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NgIf, NgFor } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';

export interface MillSelectorMill {
	code: string;
	description: string;
	isAllowChangeOfShipto?: number;
	selected: boolean;
}
@Component({
	selector: 'copo-mill-selector',
	templateUrl: './mill-selector.component.html',
	styleUrl: './mill-selector.component.css',

	imports: [TranslateModule, NgIf, MatCheckboxModule, NgFor, LoadingSpinnerComponent]
})
export class MillSelectorComponent implements OnChanges, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@Input() mills?: MillSelectorMill[];
	@Input() validateChange?: (mills: MillSelectorMill[]) => Promise<boolean>;
	@Output() readonly millsChange = new EventEmitter<MillSelectorMill[]>(true);
	isValidating = false;
	status: 'AllChecked' | 'NoChecked' | 'SomeChecked' = 'AllChecked';
	private readonly internalMillsChangeSubject: Subject<MillSelectorMill[]>;
	private readonly internalMillsChangeSubscription: Subscription;

	constructor() {
		// debounce data change to reduce API calls by parent components
		this.internalMillsChangeSubject = new Subject<MillSelectorMill[]>();
		this.internalMillsChangeSubscription = this.internalMillsChangeSubject
			.pipe(debounceTime(750))
			.subscribe(mills => {
				this.millsChange.emit(mills);
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (changes['mills']) {
			this.updateStatus();
		}
	}

	ngOnDestroy(): void {
		this.internalMillsChangeSubscription.unsubscribe();
	}

	async setAll($event: MouseEvent) {
		$event.preventDefault();
		$event.stopPropagation();
		if (!this.mills || this.isValidating) {
			return false;
		}
		const newMills = this.mills.map(mill => ({
			...mill,
			selected: this.status !== 'AllChecked'
		}));
		await this.updateMills(newMills);
		return false;
	}

	async onToggle($event: MouseEvent, millToToggle: MillSelectorMill) {
		$event.preventDefault();
		$event.stopPropagation();
		if (!this.mills || this.isValidating) {
			return false;
		}
		const newMills = this.mills.map(mill => {
			if (mill.code === millToToggle.code) {
				return {
					...mill,
					selected: !millToToggle.selected
				};
			}
			return mill;
		});
		await this.updateMills(newMills);
		return false;
	}

	private async updateMills(mills: MillSelectorMill[]) {
		const isValidated = await this.validate(mills);
		if (isValidated) {
			this.mills = mills;
			this.internalMillsChangeSubject.next(mills);
			this.updateStatus();
		}
	}

	private updateStatus() {
		if (!this.mills) {
			this.status = 'NoChecked';
			return;
		}
		const nbSelected = this.mills.filter(m => m.selected).length;
		if (nbSelected === 0) {
			this.status = 'NoChecked';
		} else if (nbSelected === this.mills.length) {
			this.status = 'AllChecked';
		} else {
			this.status = 'SomeChecked';
		}
	}

	private async validate(mills: MillSelectorMill[]) {
		if (!this.validateChange) {
			return true;
		}
		this.isValidating = true;
		const isValidated = await this.validateChange(mills);
		this.isValidating = false;
		return isValidated;
	}
}
